import { useEffect, useState } from 'react';
import { motion } from 'motion/react';
import { Xmark } from '@peacecover/icon';
import { useGetBettieRandomRewards } from '../../domains/bettie/ui/hooks/useGetBettieRandomRewards';
import { useSuccessPhotoCardToast } from '../template/edit-collected-photo-cards/hooks/useSuccessPhotoCardToast';
import { useHandleHistoryBack } from '../../utils/useHandleHistoryBack';
import { YutStick } from './components/YutStick';
import { ResultModal } from './components/ResultModal';
import { ReactComponent as GradientAttention } from '../../icons/gradient.attention.svg';

const EVENT_YUT_LAST_THROW_KEY = 'event_yut_last_throw';
const COOLDOWN_MS = 24 * 60 * 60 * 1000;

/** localStorage에서 마지막 던진 시각(ms) 조회. 기존 YYYY-MM-DD 값은 해당일 00:00 로 해석 */
function getLastThrowTimestamp(): number | null {
  try {
    const s = localStorage.getItem(EVENT_YUT_LAST_THROW_KEY);
    if (!s) return null;
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
      return new Date(`${s}T00:00:00`).getTime();
    }
    const t = Number.parseFloat(s);
    return Number.isFinite(t) ? t : null;
  } catch {
    return null;
  }
}

function setLastThrowTimestamp(ms: number): void {
  try {
    localStorage.setItem(EVENT_YUT_LAST_THROW_KEY, String(ms));
  } catch {
    // ignore
  }
}

function getNextAvailableAt(lastThrowAt: number | null): number | null {
  if (lastThrowAt === null) return null;
  return lastThrowAt + COOLDOWN_MS;
}

function canThrowNow(lastThrowAt: number | null, now: number): boolean {
  const nextAt = getNextAvailableAt(lastThrowAt);
  return nextAt === null || now >= nextAt;
}

function getRemainingMs(lastThrowAt: number | null, now: number): number {
  const nextAt = getNextAvailableAt(lastThrowAt);
  if (nextAt === null) return 0;
  return Math.max(0, nextAt - now);
}

/** 남은 시간을 "N시간 N분 N초" 형식으로 표시 */
function formatRemainingTime(ms: number): string {
  const totalSeconds = Math.ceil(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const parts: string[] = [];
  if (hours > 0) parts.push(`${hours}시간`);
  if (minutes > 0) parts.push(`${minutes}분`);
  parts.push(`${seconds}초`);
  return parts.join(' ');
}

type YutResult = 'Do' | 'Gae' | 'Geol' | 'Yut' | 'Mo';

interface GameResult {
  name: YutResult;
  hearts: number;
}

/** 서버 amount(1~5)를 윷 결과 이름으로 변환 */
function amountToYutResult(amount: number): YutResult {
  const map: Record<number, YutResult> = {
    1: 'Do',
    2: 'Gae',
    3: 'Geol',
    4: 'Yut',
    5: 'Mo'
  };
  return map[amount] ?? 'Do';
}

/** 서버 amount(1~5)를 막대 상태 4개로 변환 (1~4: 평평한 개수, 5: 모 → 0개) */
function amountToStickStates(amount: number): boolean[] {
  const flatCount = amount === 5 ? 0 : amount;
  return Array.from({ length: 4 }, (_, i) => i < flatCount);
}

function EventPage() {
  const [sticks, setSticks] = useState<boolean[]>([true, true, true, true]);
  const [isAnimating, setIsAnimating] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [gameResult, setGameResult] = useState<GameResult | null>(null);
  const [lastThrowAt, setLastThrowAt] = useState<number | null>(getLastThrowTimestamp);
  const [now, setNow] = useState(() => Date.now());

  const { mutateAsync: getBettieRandomRewards } = useGetBettieRandomRewards();
  const showSuccessToast = useSuccessPhotoCardToast();
  const handleHistoryBack = useHandleHistoryBack();

  const canThrow = canThrowNow(lastThrowAt, now);
  const remainingMs = getRemainingMs(lastThrowAt, now);

  useEffect(() => {
    if (lastThrowAt === null) return;
    const nextAt = lastThrowAt + COOLDOWN_MS;
    const id = setInterval(() => {
      const nowMs = Date.now();
      setNow(nowMs);
      if (nowMs >= nextAt) clearInterval(id);
    }, 1000);
    return () => clearInterval(id);
  }, [lastThrowAt]);

  const throwYut = async () => {
    if (!canThrow || isAnimating) return;

    setIsAnimating(true);

    try {
      const data = await getBettieRandomRewards();
      const amount = data.bettieTransaction.amount;
      const newSticks = amountToStickStates(amount);

      setTimeout(() => {
        setSticks(newSticks);
        setTimeout(() => {
          const thrownAt = Date.now();
          setLastThrowAt(thrownAt);
          setNow(thrownAt);
          setIsAnimating(false);
          setGameResult({ name: amountToYutResult(amount), hearts: amount });
          setShowResult(true);
          setLastThrowTimestamp(thrownAt);
        }, 500);
      }, 1200);
    } catch (err: unknown) {
      setIsAnimating(false);
      const errCode = (err as { error?: { code?: string } })?.error?.code;
      if (errCode === 'DailyRandomRewardAlreadyClaimed') {
        const lastAt = getLastThrowTimestamp();
        const remaining = getRemainingMs(lastAt, Date.now());
        const message =
          remaining > 0
            ? `${formatRemainingTime(remaining)} 후에 다시 던질 수 있어요`
            : '24시간 후에 다시 던질 수 있어요';
        showSuccessToast('bottom', message, false, <GradientAttention width={20} height={20} />);
      }
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center"
      style={{ backgroundColor: '#D9F0F9' }}
    >
      {/* Mobile Container */}
      <div
        className="w-full max-w-[full] min-h-[700px] rounded-[3rem] shadow-2xl overflow-hidden relative"
        style={{
          backgroundColor: '#D9F0F9',
          backgroundImage: 'url(/assets/backgrounds/background_newyear2026.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'repeat'
        }}
      >
        {/* Content */}
        <div className="relative z-10 flex flex-col h-screen px-[32px] pt-[20px] pb-[32px]">
          <header className="px-[16px] pb-[15px] pt-[calc(15px+env(safe-area-inset-top))] absolute top-0 left-0">
            <button type="button" onClick={handleHistoryBack}>
              <Xmark size={24} className="text-[#222]" />
            </button>
          </header>
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center px-[0px] pt-[20px] pb-[0px] mx-[0px] mt-[90px] mb-[10px]"
          >
            <div className="flex items-center justify-center gap-2 mb-4 bg-[#ffffff00]">
              <h1 className="font-bold text-[11px] text-[#50b0f8] bg-[#EDF7FE] px-[8px] py-[4px] rounded-[12px]">
                EVENT
              </h1>
            </div>
            <h2
              className="mb-3"
              style={{
                color: '#222',
                textAlign: 'center',
                fontFamily: 'Pretendard',
                fontSize: '26px',
                fontStyle: 'normal',
                fontWeight: 700,
                lineHeight: 'normal'
              }}
            >
              설날 맞이 하트 드려요
            </h2>
            <p className="text-[15px] text-[#4f4f4f]">설 연휴 동안 매일 한 번 참여할 수 있어요</p>
          </motion.div>

          {/* Game Area */}
          <div className="flex-1 flex items-center justify-center mx-[0px] mt-[-80px] mb-[0px]">
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              {/* Decorative circle background */}
              <div className="absolute inset-0 -m-12 rounded-full bg-gradient-to-br from-pink-100 to-blue-100 opacity-30 blur-2xl" />

              {/* Sticks container */}
              <div className="relative bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-lg bg-[#ffffff]">
                <div className="flex gap-4 justify-center items-center">
                  {sticks.map((isFlat, index) => (
                    <YutStick key={index} isFlat={isFlat} index={index} isAnimating={isAnimating} />
                  ))}
                </div>
              </div>
            </motion.div>
          </div>

          {/* Action Area */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="pb-12"
          >
            <button
              type="button"
              onClick={throwYut}
              disabled={!canThrow || isAnimating}
              className={`w-full rounded-full font-bold text-lg shadow-lg transition-all duration-300 ${!canThrow || isAnimating ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-gradient-to-r from-red-500 to-orange-500 text-white hover:shadow-xl hover:scale-105 active:scale-95'} px-[0px] py-[16px]`}
            >
              {isAnimating ? '던지는 중...' : '윷 던지고 하트 받기'}
            </button>

            <p className="text-center mt-4 text-sm text-gray-600">
              {canThrow ? (
                <>
                  오늘 <span className="font-semibold text-pink-500">1</span>번 던질 수 있어요
                </>
              ) : (
                <span className="text-gray-400">
                  <span className="font-semibold text-pink-500">
                    {formatRemainingTime(remainingMs)}
                  </span>{' '}
                  후에 다시 던질 수 있어요
                </span>
              )}
            </p>
          </motion.div>
        </div>

        {/* Result Modal */}
        {gameResult && (
          <ResultModal
            isOpen={showResult}
            result={gameResult.name}
            hearts={gameResult.hearts}
            onClose={() => setShowResult(false)}
          />
        )}
      </div>
    </div>
  );
}

export default EventPage;
